import React, { createContext, useContext, useMemo, useReducer } from 'react';

import { buildInitialState } from '../core/context-initial-state-builder.core';
import type { ProductOptionActions, ProductOptionContextType, ProductOptionState } from '../type/common.type';
import { productOptionReducer } from './productOptionReducer';

const ProductOptionContext = createContext<ProductOptionContextType | null>(null);

interface ProductOptionProviderProps {
  children: React.ReactNode;
  initialState?: ProductOptionState;
}

export const ProductOptionProvider: React.FC<ProductOptionProviderProps> = ({ children, initialState }) => {
  const [state, dispatch] = useReducer(productOptionReducer, {
    ...buildInitialState(),
    ...initialState,
  });

  const actions = useMemo<ProductOptionActions>(
    () => ({
      initializeOption: (payload) => {
        dispatch({ type: 'INITIALIZE_OPTION', payload });
      },
      setOptionMap: (payload) => {
        dispatch({ type: 'SET_OPTION_MAP', payload });
      },
      resetSelection: (payload) => {
        dispatch({ type: 'RESET_SELECTION', payload });
      },
      closeAllDropdowns: () => {
        dispatch({ type: 'CLOSE_ALL_DROPDOWNS', payload: undefined });
      },
      toggleDropdown: (payload) => {
        dispatch({ type: 'TOGGLE_DROPDOWN', payload });
      },
      toggleAdditionalDropdown: () => {
        dispatch({ type: 'TOGGLE_ADDITIONAL_DROPDOWN', payload: undefined });
      },
      handleClickDropdown: (payload) => {
        dispatch({ type: 'HANDLE_CLICK_DROPDOWN', payload });
      },
      toggleShippingFilter: (payload) => {
        dispatch({ type: 'TOGGLE_SHIPPING_FILTER', payload });
      },
      continueOptionSelection: (payload) => {
        dispatch({ type: 'CONTINUE_OPTION_SELECTION', payload });
      },
      addItemCard: (payload) => {
        dispatch({ type: 'ADD_ITEM_CARD', payload });
      },
      removeItemCard: (payload) => {
        dispatch({ type: 'REMOVE_ITEM_CARD', payload });
      },
      incrementItemCount: (payload) => {
        dispatch({ type: 'INCREMENT_ITEM_COUNT', payload });
      },
      decrementItemCount: (payload) => {
        dispatch({ type: 'DECREMENT_ITEM_COUNT', payload });
      },
      setRestockMode: (payload) => {
        dispatch({ type: 'SET_RESTOCK_MODE', payload });
      },
      updateRestockNotificationStartIndex: (payload) => {
        dispatch({ type: 'UPDATE_RESTOCK_NOTIFICATION_START_INDEX', payload });
      },
      updateWmsNotificationInfo: (payload) => {
        dispatch({ type: 'UPDATE_WMS_NOTIFICATION_INFO', payload });
      },
      updateRestockNotificationStatus: (payload) => {
        dispatch({ type: 'UPDATE_RESTOCK_NOTIFICATION_STATUS', payload });
      },
      resetOptionView: (payload) => {
        dispatch({ type: 'RESET_OPTION_VIEW', payload });
      },
      resetRestockNotification: () => {
        dispatch({ type: 'RESET_RESTOCK_NOTIFICATION', payload: undefined });
      },
      updateCustomInputState: (payload) => {
        dispatch({ type: 'UPDATE_CUSTOM_INPUT_STATE', payload });
      },
      resetAllCustomInputs: () => {
        dispatch({ type: 'RESET_ALL_CUSTOM_INPUTS', payload: undefined });
      },
    }),
    [dispatch],
  );

  const contextValue = useMemo<ProductOptionContextType>(
    () => ({
      state,
      dispatch,
      actions,
    }),
    [state, actions],
  );

  return <ProductOptionContext.Provider value={contextValue}>{children}</ProductOptionContext.Provider>;
};

export const useProductOptionContext = (): ProductOptionContextType => {
  const context = useContext(ProductOptionContext);

  if (!context) {
    throw new Error('useProductOptionContext must be used within a ProductOptionProvider');
  }

  return context;
};
